/* eslint-disable react-hooks/rules-of-hooks */
import './Dashboard.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
    ApplicationItem,
    getStatusTransitions,
    loadApplications,
    mapApplication,
    StatusHistory,
    updateApplication
} from '../services/applicationsApi';
import {urnValidator, UserInfo} from '../services/common';
import {useMsal} from '@azure/msal-react';
import React from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowSelectionState,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import {
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {FaEdit} from 'react-icons/fa';
import AppDetails from '../components/AppDetails';
import StatusChangeWarning from '../components/StatusChangeWarning';
import InfoModal from '../components/InfoModal';
import PowerBiReport from '../components/PowerBiReport';
import {PowerBiReportType} from '../services/powerBiApi';
import RequestAccess from '../components/RequestAccess';
import InfoMain from '../components/InfoMain';
import HtmlTooltip from "../components/HtmlTooltip";

interface MainProps {
    userInfo: UserInfo;
}

const Main = ({ userInfo }: MainProps) => {
    const { instance } = useMsal();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 0-900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900-1200px
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // 0-600px
    
    const [selectedRow, setSelectedRow] = React.useState<RowSelectionState>();
    const [editItem, setEditItem] = React.useState<ApplicationItem>();
    const [originalItem, setOriginalItem] = React.useState<ApplicationItem>();
    const [isViewMode, setIsViewMode] = React.useState<boolean>(true);
    const [data, setData] = React.useState<ApplicationItem[]>([]);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [filter1, setFilter1] = React.useState('');
    const [filter2, setFilter2] = React.useState('');
    const [filter3, setFilter3] = React.useState('');
    const [debouncedFilters, setDebouncedFilters] = React.useState(['', '', '']);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [isWarningOpen, setIsWarningOpen] = React.useState(false);
    const [isInfoOpen, setIsInfoOpen] = React.useState(false);
    const [isUrnValid, setIsUrnValid] = React.useState(true);
    const [statusTransitions, setStatusTransitions] = React.useState<Map<string, string[]>>();

    const [myAppsOnly, setMyAppsOnly] = React.useState(true);
    const canShowCheckbox = userInfo?.isAppOwner === true

    const handleOpen = (appInstanceId: string) => {
        setIsViewMode(true);
        const selectedItem = data.find(x => x.appInstanceId == appInstanceId);
        setOriginalItem(JSON.parse(JSON.stringify(selectedItem)));
        setEditItem(selectedItem);
        setIsFormOpen(true);
        validateUrn(selectedItem.urn);
    };

    const handleSave = async (checkStatus: boolean = true) => {
        if (checkStatus) {
            const checkResult = await shouldAutoApproveStatusChange();
            if (!checkResult) {
                return;
            }
        }
        const oldStatus = originalItem?.status;
        const existingHistory = editItem?.statusHistory ? [...editItem.statusHistory] : [];
        const updatedApp = await updateApplication(instance, editItem);
        await handleElementUpdate(updatedApp, true);
        if (oldStatus !== updatedApp.status && oldStatus != null && updatedApp.status != null) {
            const newEntry: StatusHistory = {
                isForgerockStatusActive: editItem?.isForgerockStatusValid ?? false,
                targetEnv: editItem?.targetEnvironment ?? '',
                urn: editItem?.urn ?? '',
                protocol: editItem?.authenticationMethod ?? '',
                oldMigrationStatus: oldStatus,
                newMigrationStatus: updatedApp.status,
                appInstanceId: editItem?.appInstanceId ?? '',
                entraAppId: editItem?.entraAppId ?? '',
                statusChangedDate: new Date(),
                statusChangedEmail: userInfo?.email ?? ''
            };
            editItem.statusHistory = [...existingHistory, newEntry];
        } else {
            editItem.statusHistory = existingHistory;
        }
    };

    const handleElementUpdate = async (updatedApp: ApplicationItem, closeModal: boolean) => {
        await mapApplication(updatedApp, editItem);
        await mapApplication(updatedApp, originalItem);
        setData(data.map(item => item));
        if (closeModal)
        {
            setIsFormOpen(false);
        }
    };

    const shouldAutoApproveStatusChange = async (): Promise<boolean> => {
        if (editItem == null) {
            return false;
        }
        if (editItem.status === editItem.originalStatus) {
            return true;
        }
        if (editItem.status === 'Confirm Cutover') {
            setIsFormOpen(false);
            setIsWarningOpen(true);
            return false;
        }
        if (editItem.status === 'Complete') {
            setIsFormOpen(false);
            setIsWarningOpen(true);
            return false;
        }
        if (editItem.status === 'To Be Decommissioned') {
            setIsFormOpen(false);
            setIsWarningOpen(true);
            return false;
        }
        return true;
    }

    const handleClose = async () =>
    {
        await mapApplication(originalItem, editItem);
        setIsFormOpen(false);
    }

    const handleWarningCancel = async () => {
        setIsViewMode(false);
        setIsWarningOpen(false);
        setIsFormOpen(true);
    }

    const handleWarningConfirm = async () => {
        setIsWarningOpen(false);
        await handleSave(false);
        if (editItem.status === "To Be Decommissioned") {
            setIsInfoOpen(true);
        }
    }

    const handleInfoClose = async () => {
        setIsInfoOpen(false);
    }

    const getStatusColor = (status: string | null): string => {
        if (status == null) { return "#BABABA"; }
        if (status == "Not Ready") { return "#696969"; }
        if (status == "Awaiting Initiation") { return "#21812D"; }
        if (status == "Processing") { return "#21812D"; }
        if (status == "Ready") { return "#21812D"; }
        if (status == "AuthN Detected") { return "#FFBF1F"; }
        if (status == "Confirm Cutover") { return "#FD6412"; }
        if (status == "Complete") { return "#7AC282"; }
        if (status == "To Be Decommissioned") { return "#000000"; }
        if (status == "Decommissioned") { return "#000000"; }
        if (status == "Failed") { return "#C52A1A"; }
        return "red";
    }

    // Responsive column configuration
    const getVisibleColumns = () => {
        const allColumns: ColumnDef<ApplicationItem>[] = [
            {
                accessorKey: 'name',
                header: 'Name',
                filterFn: 'includesString',
                size: isMobile ? 150 : 180,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.name}</Typography>}>
                            <div style={{ 
                                overflow: 'hidden', 
                                whiteSpace: 'nowrap', 
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '120px' : '160px'
                            }}>
                                {row.original.name}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'ci',
                header: 'CI',
                filterFn: 'includesString',
                size: isMobile ? 70 : 90,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.ci}</Typography>}>
                            <div style={{ 
                                overflow: 'hidden', 
                                whiteSpace: 'nowrap', 
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '60px' : '80px'
                            }}>
                                {row.original.ci}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'urn',
                header: 'URN',
                filterFn: 'includesString',
                size: isMobile ? 120 : 150,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.urn}</Typography>}>
                            <div style={{ 
                                overflow: 'hidden', 
                                whiteSpace: 'nowrap', 
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '100px' : '130px'
                            }}>
                                {row.original.urn}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'targetEnvironment',
                header: 'Env',
                filterFn: 'includesString',
                size: isMobile ? 50 : 80,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.targetEnvironment}</Typography>}>
                            <Chip
                                label={row.original.targetEnvironment}
                                size="medium"
                                variant="filled"
                                sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    height: '36px',
                                    backgroundColor: '#e3e3e3',
                                }}
                            />
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'authenticationMethod',
                header: 'Protocol',
                filterFn: 'includesString',
                size: 100,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.authenticationMethod}</Typography>}>
                            <div style={{ 
                                overflow: 'hidden', 
                                whiteSpace: 'nowrap', 
                                textOverflow: 'ellipsis',
                                maxWidth: '80px'
                            }}>
                                {row.original.authenticationMethod}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'territoryName',
                header: 'Territory',
                filterFn: 'includesString',
                size: 100,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.territoryName}</Typography>}>
                            <div style={{ 
                                overflow: 'hidden', 
                                whiteSpace: 'nowrap', 
                                textOverflow: 'ellipsis',
                                maxWidth: '80px'
                            }}>
                                {row.original.territoryName}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'memberFirm',
                header: 'Member Firm',
                filterFn: 'includesString',
                size: isMobile ? 90 : 120,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={<Typography color="inherit">{row.original.memberFirm}</Typography>}>
                            <div style={{
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '120px' : '120px'
                            }}>
                                {row.original.memberFirm}
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                filterFn: 'includesString',
                size: isMobile ? 100 : 130,
                cell: ({ row }) => {
                    return (
                        <HtmlTooltip placement="top" title={
                            <Typography color="inherit">
                                {row.original.statusDescription || "Application status is not yet known, waiting for calculation"}
                            </Typography>
                        }>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                overflow: 'hidden',
                                maxWidth: isMobile ? '80px' : '110px'
                            }}>
                                <CircleIcon 
                                    htmlColor={getStatusColor(row.original.status)} 
                                    sx={{ 
                                        marginRight: '4px', 
                                        fontSize: isMobile ? '12px' : '16px'
                                    }} 
                                />
                                <span style={{ 
                                    overflow: 'hidden', 
                                    whiteSpace: 'nowrap', 
                                    textOverflow: 'ellipsis',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>
                                    {row.original.status || "N/A"}
                                </span>
                            </div>
                        </HtmlTooltip>
                    );
                },
            },
            {
                accessorKey: 'statusChangedDate',
                header: 'Last Change',
                size: isMobile ? 80 : 100,
                cell: ({ row }) => {
                    const statusChangedDate = row.original.statusChangedDate ? new Date(row.original.statusChangedDate) : null;
                    let daysAgo = '';
                    if (statusChangedDate) {
                        const now = new Date();
                        const utc1 = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
                        const utc2 = Date.UTC(statusChangedDate.getFullYear(), statusChangedDate.getMonth(), statusChangedDate.getDate());
                        const msPerDay = 24 * 60 * 60 * 1000;
                        const diffDays = Math.floor((utc1 - utc2) / msPerDay);
                        daysAgo = diffDays === 0 ? 'Today' : `${diffDays}d ago`;
                    } else {
                        daysAgo = 'N/A';
                    }
                    return (
                        <div style={{ 
                            fontSize: isMobile ? '11px' : '13px',
                            overflow: 'hidden', 
                            whiteSpace: 'nowrap', 
                            textOverflow: 'ellipsis'
                        }}>
                            {daysAgo}
                        </div>
                    );
                },
            },
            {
                accessorKey: 'actions',
                header: 'Actions',
                size: 60,
                cell: ({ row }) => {
                    return (
                        <button 
                            onClick={() => handleOpen(row.original.appInstanceId)} 
                            className="icon-button edit-button"
                            style={{ 
                                padding: isMobile ? '4px' : '8px',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer'
                            }}
                        >
                            <FaEdit size={isMobile ? "16" : "20"} />
                        </button>
                    );
                },
            },
        ];
        return allColumns;
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters([filter1, filter2, filter3]);
        }, 300);

        return () => clearTimeout(timer);
    }, [filter1, filter2, filter3]);

    const indexedData = React.useMemo(() => {
        const columns = getVisibleColumns();
        const accessorKeys = columns
            .map(col => 'accessorKey' in col ? col.accessorKey : null)
            .filter((key): key is string => typeof key === 'string' && key !== 'actions');

        return data.map(row => ({
            ...row,
            _searchIndex:accessorKeys
                .map(key => row[key])
                .filter(value => value != null)
                .join(' ')
                .toLowerCase()
        }));
    }, [data]);

    const customGlobalFilterFn = React.useCallback((row, columnId, filterValue) => {
        const filters = filterValue.filter(f => f && f.trim() !== '');

        if (filters.length === 0) return true;

        return filters.every(filter =>
            row.original._searchIndex.includes(filter.toLowerCase())
        );
    }, []);

    const table = useReactTable({
        data: indexedData,
        columns: getVisibleColumns(),
        state: {
            sorting,
            globalFilter: debouncedFilters,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: customGlobalFilterFn,
        autoResetPageIndex: false,
        initialState: {
            pagination: {
                pageSize: isMobile ? 5 : 10,
            },
        },
        getRowId: row => row.appInstanceId,
        onRowSelectionChange: setSelectedRow,
        enableRowSelection: true,
        enableMultiRowSelection: false,
    });

    const loadData = async () => {
        setIsLoading(true);
        try {
            if (userInfo?.canSeeApps === true) {
                setStatusTransitions(await getStatusTransitions(instance));
                const apps = await loadApplications(instance, myAppsOnly);
                setData(apps);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const validateUrn = (newValue) => {
        const urnValidationError = urnValidator(newValue);
        setIsUrnValid(!!urnValidationError === false);
    };

    React.useEffect(() => {
        loadData();
    }, [userInfo]);

    React.useEffect(() => {
        loadData();
    }, [myAppsOnly]);

    React.useEffect(() => {
        if (selectedRow) {
            const selectedId = Object.keys(selectedRow)[0];
            handleOpen(selectedId);
        }
    }, [selectedRow]);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <div className="settings-content" style={{ width: '100%' }}>
                    <h1>Welcome to the IDaaS Migration Portal</h1>
                    <p style={{ 
                        textAlign: 'left', 
                        margin: '0px', 
                        marginTop: '50px', 
                        fontSize: isMobile ? '16px' : '18px', 
                        maxWidth: '1400px' 
                    }}>
                        This is your central hub for <b>tracking the live status of your application's migration from PwC Identity to Entra ID</b><br/>
                        and answering any questions you have along the way.<br/>
                        <br />
                        Try to use our AI chat (bottom right corner) or send us an email at: <a style={{ color: '#FD5107' }} href="mailto:us_gbl-nis-integration@pwc.com" target="_blank" >us_gbl-nis-integration@pwc.com</a>
                    </p>
                </div>
                {userInfo?.canSeeApps === true && (
                    <div className="settings-content" style={{ width: '100%' }}>
                        <div className="app-list-title" style={{ fontSize: isMobile ? '20px' : '24px' }}>
                            Application inventory
                        </div>
                        <div className="settings-table-container">
                            <div className="table-controls">
                                <div className="filter-input-container">
                                    <input
                                        type="text"
                                        placeholder="Filter..."
                                        value={filter1}
                                        onChange={(e) => setFilter1(e.target.value)}
                                        className="filter-input"
                                        style={{
                                            width: isMobile ? '100%' : 'auto',
                                            marginBottom: isMobile ? '10px' : '0'
                                        }}
                                    />

                                    <span> and </span>

                                    <input
                                        type="text"
                                        placeholder="Filter..."
                                        value={filter2}
                                        onChange={(e) => setFilter2(e.target.value)}
                                        className="filter-input"
                                        style={{
                                            width: isMobile ? '100%' : 'auto',
                                            marginBottom: isMobile ? '10px' : '0'
                                        }}
                                    />

                                    <span> and </span>

                                    <input
                                        type="text"
                                        placeholder="Filter..."
                                        value={filter3}
                                        onChange={(e) => setFilter3(e.target.value)}
                                        className="filter-input"
                                        style={{
                                            width: isMobile ? '100%' : 'auto',
                                            marginBottom: isMobile ? '10px' : '0'
                                        }}
                                    />
                                </div>
                                {canShowCheckbox &&
                                    (<label
                                    className="my-apps-filter"> <input type="checkbox" checked={myAppsOnly} onChange={(e) => setMyAppsOnly(e.target.checked)} /> View my owned apps only
                                </label>)}
                            </div>
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: isMobile ? '60vh' : '70vh',
                                    overflowX: 'auto'
                                }}
                            >
                                <Table stickyHeader>
                                    <TableHead>
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <TableCell
                                                        key={header.id}
                                                        style={{
                                                            fontWeight: 'bold',
                                                            fontSize: '1.1rem', // Larger font
                                                            padding: '18px 12px',
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: ' 🔼',
                                                            desc: ' 🔽',
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={getVisibleColumns().length} align="center" style={{ padding: '20px', fontSize: '1.1rem' }}>
                                                    Loading...
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            table.getRowModel().rows.map(row => (
                                                <TableRow key={row.id} hover style={{ cursor: 'pointer' }}>
                                                    {row.getVisibleCells().map(cell => (
                                                        <TableCell
                                                            key={cell.id}
                                                            style={{
                                                                fontSize: '1.05rem', // Larger font
                                                                padding: '16px 10px',
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div 
                                className="pagination-controls" 
                                style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    marginTop: '16px'
                                }}
                            >
                                <button
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage()}
                                    style={{ 
                                        padding: isMobile ? '4px 8px' : '8px 12px',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}
                                >
                                    {'<<'}
                                </button>
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    style={{ 
                                        padding: isMobile ? '4px 8px' : '8px 12px',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}
                                >
                                    {'<'}
                                </button>
                                <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                    Page{' '}
                                    <strong>
                                        {table.getState().pagination.pageIndex + 1} of{' '}
                                        {table.getPageCount()}
                                    </strong>
                                </span>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    style={{ 
                                        padding: isMobile ? '4px 8px' : '8px 12px',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}
                                >
                                    {'>'}
                                </button>
                                <button
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={!table.getCanNextPage()}
                                    style={{ 
                                        padding: isMobile ? '4px 8px' : '8px 12px',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}
                                >
                                    {'>>'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {userInfo?.hasAnyRole === true ? (
                    <div className="settings-content" style={{ width: '100%' }}>
                        <PowerBiReport reportType={PowerBiReportType.Overview} />
                    </div>
                ) : userInfo === null ? '' : <RequestAccess/>}
            </div>
            <AppDetails 
                handleClose={handleClose} 
                handleSave={handleSave} 
                isModalOpen={isFormOpen} 
                isUrnValid={isUrnValid} 
                isViewMode={isViewMode} 
                model={editItem} 
                setIsViewMode={setIsViewMode} 
                userInfo={userInfo} 
                statusTransitions={statusTransitions} 
                handleElementUpdate={handleElementUpdate} 
            />
            <StatusChangeWarning 
                isVisible={isWarningOpen} 
                toStatus={editItem?.status} 
                handleCancel={handleWarningCancel} 
                handleOk={handleWarningConfirm} 
                appName={editItem?.name} 
            />
            <InfoModal isVisible={isInfoOpen} handleClose={handleInfoClose} />
            <InfoMain />
        </div>
    );
};

export default Main;